import { useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

import { useGetOnlineDoctorsList } from '@api/home/index.rq';
import { routeList } from '@routes/routeList';
import { CarouselTexts } from '@constant/home';
import { Button } from '@com/_atoms/NewButton';
import Icon from '@utilities/icon';
import { colors } from '@configs/Theme';
import VerticalDoctorShimmer from '@com/shared/cards/doctor/vertical-doctor-shimmer';
import VerticalDoctorCard from '@com/shared/cards/doctor/vertical-doctor-card';
import useInView from '@hooks/useInView';

const ScrollSlider = dynamic(() => import('@com/shared/scroll-slider'));

interface Props {
  backgroundColor?: string;
  headerBackgroundColor?: string;
  moreLinkTitle?: string;
  carouselCardClassName?: string;
  containerClassName?: string;
  scrollSliderClassName?: string;
  isShowMoreButton?: boolean;
  isTwoRow?: boolean;
}

const shimerData = [...Array(10).keys()];

const OnlineDoctorsCarousel = ({
  backgroundColor,
  headerBackgroundColor,
  carouselCardClassName,
  containerClassName,
  scrollSliderClassName,
  isShowMoreButton = true,
  isTwoRow,
}: Props) => {
  const { ref, isInView } = useInView();
  const { push } = useRouter();
  const { data, isLoading } = useGetOnlineDoctorsList(
    { pageSize: 24 },
    {
      enabled: isInView,
    },
  );

  const [routeLoadingId, setRouteLoadingId] = useState<number | null>(null);

  const doctors = data?.pages?.[0]?.queryResult ?? [];

  const onClickHandler = (recId: number) => {
    if (!recId) return;
    setRouteLoadingId(recId);
    push(`${routeList?.doctorProfile}/${recId}?fromHome=true`);
  };
  return (
    <div
      ref={ref}
      className={classNames(
        'mt-3 flex flex-col overflow-visible',
        !isTwoRow && 'h-[268px] pb-2',
        isTwoRow && 'pb-2',
        containerClassName,
      )}
      style={
        backgroundColor
          ? { backgroundColor: backgroundColor ? backgroundColor : '' }
          : {}
      }
    >
      <div
        className={classNames(
          'h-[48px] flex items-center justify-between px-4',
        )}
        style={
          headerBackgroundColor
            ? {
                backgroundColor: headerBackgroundColor && headerBackgroundColor,
              }
            : {}
        }
      >
        <div className="flex items-center">
          <span
            className={classNames('text-Label/Medium text-content-primary')}
          >
            پزشک های آنلاین{' '}
            <span className="text-xs text-content-tertiary">
              ({data?.pages[0]?.totalCount} پزشک)
            </span>
          </span>
        </div>

        {isShowMoreButton && (
          <Button
            variant="secondary"
            size="medium"
            className="h-10 w-10 rounded-full !p-0"
            aria-label="More"
            onClick={() =>
              push(
                `${routeList?.ocsOnlineDoctors}?fromHome=true&pageNumber=1&pageSize=20`,
              )
            }
            icon={
              <Icon
                name="ChevronLeft"
                width={1.5}
                height={1.5}
                fill={colors.black}
              />
            }
          />
        )}
      </div>

      <ScrollSlider
        className={classNames(
          !isShowMoreButton && 'pl-4',
          'px-4',
          isTwoRow
            ? 'overflow-y-visible pt-4 pb-2'
            : 'items-center gap-x-2',
          scrollSliderClassName,
        )}
      >
        {isTwoRow ? (
          <div className="grid grid-flow-col auto-cols-[152px] grid-rows-2 gap-x-2 gap-y-4 pb-1">
            {isLoading
              ? shimerData.map((item) => (
                  <VerticalDoctorShimmer
                    key={item}
                    className="h-[204px] w-[152px]"
                  />
                ))
              : doctors.map((item: any) => (
                  <VerticalDoctorCard
                    key={item?.recId}
                    onClick={() => onClickHandler(item?.recId)}
                    isLoading={
                      !!routeLoadingId && item?.recId === routeLoadingId
                    }
                    className={carouselCardClassName}
                    imageUrl={item?.imageUrl}
                    star={item?.rate}
                    title={item?.fullName}
                    subtitle={item?.latestSpecialityName}
                    activity={item?.activityStatus}
                    isOndemand
                    isScheduled
                  />
                ))}

            {isShowMoreButton && (
              <div
                onClick={() =>
                  push(
                    `${routeList?.ocsOnlineDoctors}?fromHome=true&pageNumber=1&pageSize=20`,
                  )
                }
                className="row-span-2 flex min-w-[120px] cursor-pointer flex-col items-center justify-center gap-y-2 self-center text-Label/xSmall text-content-primary"
                onKeyDown={() => undefined}
                tabIndex={0}
                role="button"
              >
                <div className="flex h-10 w-10 min-w-10 items-center justify-center rounded-full bg-surface-tertiary">
                  <Icon
                    name="ArrowLeft"
                    width={1.25}
                    height={1.25}
                    fill={colors.black}
                  />
                </div>
                {CarouselTexts?.viewAll}
              </div>
            )}
          </div>
        ) : (
          <>
            {isLoading
              ? shimerData.map((item) => (
                  <VerticalDoctorShimmer key={item} className="h-[204px] w-[152px]" />
                ))
              : doctors.map((item: any) => (
                  <VerticalDoctorCard
                    key={item?.recId}
                    onClick={() => onClickHandler(item?.recId)}
                    isLoading={
                      !!routeLoadingId && item?.recId === routeLoadingId
                    }
                    className={carouselCardClassName}
                    imageUrl={item?.imageUrl}
                    star={item?.rate}
                    title={item?.fullName}
                    subtitle={item?.latestSpecialityName}
                    activity={item?.activityStatus}
                    isOndemand
                    isScheduled
                  />
                ))}

            {isShowMoreButton && (
              <div
                onClick={() =>
                  push(
                    `${routeList?.ocsOnlineDoctors}?fromHome=true&pageNumber=1&pageSize=20`,
                  )
                }
                className="flex h-[204px] min-w-[152px] cursor-pointer flex-col items-center justify-center gap-y-2 rounded-lg bg-surface-primary text-Label/xSmall text-content-primary shadow-fab"
                onKeyDown={() => undefined}
                tabIndex={0}
                role="button"
              >
                <Icon
                  name="ArrowLeft"
                  width={1.25}
                  height={1.25}
                  fill={colors.black}
                />
                {CarouselTexts?.viewAll}
              </div>
            )}
          </>
        )}
      </ScrollSlider>
    </div>
  );
};
export default OnlineDoctorsCarousel;
